import { Fragment, ReactNode } from 'react';
import 'app/shared/map_options/map_options.scss';
import './asset_options.scss';
import { Option } from 'app/shared/components/ims_object_selector/ims_item_selector';
import {
    ImsItemSelectorAdapter,
    selectorOptionTypes,
} from 'app/shared/components/ims_object_selector/ims_item_selector_adapter';
import { ColorLegendButton } from 'app/shared/components/color_legend/color_legend_button';
import { MapOptionGroup } from 'app/shared/map_options/MapOptionsService';
import { IMSVisualiser } from 'app/views/visualiser';
import {
    COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_WIFI_TAG,
    COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_CELLULAR_TAG,
    COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_OTHER_TAG,
    COLOR_LEGEND_ASSET_OPTIONS_TAG,
} from 'app/center/asset_panel/asset_options/asset_options.component';
import { doVisualiserPointsExist } from 'app/shared/components/color_legend/ColorLegendService';
import { Checkbox } from 'app/shared/components/checkbox/checkbox.component';

export enum mapOptionTypes {
    dropdown = 'dropdown',
    checkbox = 'checkbox',
    group = 'group',
}

export interface RenderableMapOption {
    type: mapOptionTypes;
    label: string;
    description?: string;
    value: any;
    order?: number;
    onChange?: (value) => void;
    disabled?: boolean;
    fullWidth?: boolean;
    displayButton?: boolean;
    displayButtonDescription?: string;
    displayButtonPanelTag?: string;
    groupAs?: MapOptionGroup;
}

const MapOptionDropdown = (
    mapOption: RenderableMapOption,
    visualisers: {
        asset: IMSVisualiser;
        wifiConnections: IMSVisualiser;
        cellularConnections: IMSVisualiser;
        otherConnections: IMSVisualiser;
    },
    openColorLegend?: (tag: string) => void,
): ReactNode => {
    const label = mapOption.label;
    const options: Option[] = mapOption.value.options || [];
    const button = mapOption.displayButton;

    const dropdownDisabled = options.length <= 1 || mapOption.disabled;

    function getVisualiser() {
        let visualiser;
        switch (mapOption.displayButtonPanelTag) {
            case COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_WIFI_TAG:
                visualiser = visualisers.wifiConnections;
                break;
            case COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_CELLULAR_TAG:
                visualiser = visualisers.cellularConnections;
                break;
            case COLOR_LEGEND_ASSET_OPTIONS_CONNECTIVITY_OTHER_TAG:
                visualiser = visualisers.otherConnections;
                break;
            case COLOR_LEGEND_ASSET_OPTIONS_TAG:
                visualiser = visualisers.asset;
                break;
            default:
                visualiser = null;
        }
        return visualiser;
    }

    const visualiser = getVisualiser();

    const visualiserPointsExists = doVisualiserPointsExist(visualiser);

    const buttonDisabled = dropdownDisabled || !visualiserPointsExists;

    return (
        <Fragment>
            <div className="center-select-group">
                <label>{label}</label>
                <ImsItemSelectorAdapter
                    rawOptions={options}
                    optionType={selectorOptionTypes.ASSET_OPTIONS_PANEL}
                    onChange={mapOption.onChange}
                    selectedId={mapOption.value.selected}
                    disabled={dropdownDisabled}
                    extraClasses={`${label.replace(' ', '-').toLowerCase()}`}
                />
            </div>
            {button
                ? MapOptionButton(
                      () => openColorLegend(mapOption.displayButtonPanelTag),
                      buttonDisabled,
                  )
                : null}
        </Fragment>
    );
};

const MapOptionCheckbox = (mapOption: RenderableMapOption): ReactNode => {
    const label = mapOption.label;
    const checked = mapOption.value;

    return (
        <div className="center-select-group map-option-check-box">
            <label>{label}</label>
            <Checkbox checked={checked} onChange={mapOption.onChange} />
        </div>
    );
};

const MapOptionButton = (openColorLegend: () => void, disabled: boolean): ReactNode => {
    return <ColorLegendButton onClick={openColorLegend} isDisabled={disabled} />;
};

function MapOptionGroup(
    mapOption: RenderableMapOption,
    visualisers: {
        asset: IMSVisualiser;
        wifiConnections: IMSVisualiser;
        cellularConnections: IMSVisualiser;
        otherConnections: IMSVisualiser;
    },
    openColorLegend?: (tag: string) => void,
): ReactNode {
    const mapOptions = mapOption.value;
    return (
        <div className="center-grouping">
            {mapOptions.map((option) => MapOption(option, visualisers, openColorLegend))}
        </div>
    );
}

const COMPONENT_LOOKUP = {
    [mapOptionTypes.dropdown]: MapOptionDropdown,
    [mapOptionTypes.checkbox]: MapOptionCheckbox,
    [mapOptionTypes.group]: MapOptionGroup,
};

function MapOption(
    mapOption: RenderableMapOption,
    visualisers: {
        asset: IMSVisualiser;
        wifiConnections: IMSVisualiser;
        cellularConnections: IMSVisualiser;
        otherConnections: IMSVisualiser;
    },
    openColorLegend?: (tag: string) => void,
): ReactNode {
    return (
        <div className="center-select-row">
            {mapOption.type === mapOptionTypes.dropdown || mapOption.type === mapOptionTypes.group
                ? COMPONENT_LOOKUP[mapOption.type](mapOption, visualisers, openColorLegend)
                : COMPONENT_LOOKUP[mapOption.type](mapOption)}
        </div>
    );
}
